import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { arrow, autoUpdate, flip, FloatingArrow, FloatingPortal, offset as offsetMiddleware, shift, useClick, useDismiss, useFloating, useFocus, useInteractions, } from "@floating-ui/react";
import { cloneElement, useContext, useMemo, useRef } from "react";
import { zIndexLayers } from "../../../common/zIndexLayers";
import { UISettingsProvider } from "../../../core/context/UISettingsProvider";
import { useHybridState } from "../../../core/hooks/useHybridState";
import { useArrow } from "../hooks/useArrow";
import { useHoverController } from "../hooks/useHoverController";
import { useTransition } from "../hooks/useTransition";
import { withValidation } from "../validation";
import { Content } from "./Styled";
const Component = ({ children, content, offset, delay, placement = "top", strategy, small, maxWidth = 290, colorType = "dark", textAlign = "left", hideArrow, interactive, open, onOpenChange, clickable = false, focusable = true, hoverable = true, hideOnClick = false, hideOnClickOutside = true, appendTo, zIndex = zIndexLayers.tooltip, }) => {
    const contextSettings = useContext(UISettingsProvider);
    const [isOpen, setIsOpen] = useHybridState({
        defaultValue: false,
        value: open,
        onChange: onOpenChange,
    });
    const arrowRef = useRef(null);
    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: placement || contextSettings?.tooltips?.placement,
        strategy: strategy || contextSettings?.tooltips?.popperOptions?.strategy,
        whileElementsMounted: autoUpdate,
        middleware: [
            offsetMiddleware(offset ?? 10),
            flip(),
            shift(),
            !hideArrow &&
                arrow({
                    element: arrowRef,
                }),
        ],
    });
    const arrowProps = useArrow(colorType);
    const hover = useHoverController({ context, hoverable, delay, interactive });
    const dismiss = useDismiss(context, { enabled: hideOnClickOutside });
    const focus = useFocus(context, { enabled: focusable });
    const click = useClick(context, { enabled: clickable });
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, click, dismiss, focus]);
    const { isMounted, styles } = useTransition(context);
    const style = useMemo(() => ({
        ...floatingStyles,
        ...styles,
        ...(interactive ? {} : { pointerEvents: "none", userSelect: "none" }),
        zIndex,
    }), [floatingStyles, interactive, styles, zIndex]);
    const tooltipContent = (_jsx("div", { ref: refs.setFloating, ...getFloatingProps(), style: style, children: _jsxs(Content, { "$small": small, "$colorType": colorType, "$maxWidth": maxWidth, "$textAlign": textAlign, onClick: hideOnClick ? () => setIsOpen(false) : undefined, children: [!hideArrow && _jsx(FloatingArrow, { ref: arrowRef, context: context, ...arrowProps }), content] }) }));
    const child = useMemo(() => {
        return cloneElement(children, {
            ...getReferenceProps(),
            ref: refs.setReference,
        });
    }, [children, getReferenceProps, refs.setReference]);
    const appendToValue = appendTo || contextSettings?.tooltips?.appendTo;
    return (_jsxs(_Fragment, { children: [child, isMounted &&
                (appendToValue === "parent" ? (tooltipContent) : (_jsx(FloatingPortal, { root: appendToValue, children: tooltipContent })))] }));
};
export const Tooltip = withValidation(Component);
